import { useState } from 'react';
import { BarChart } from '../../charts/BarChart';
import { BarList, Card, ChartCard, DataTable, Segmented, Stat, StatusChip } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { api } from '../../lib/api';
import { num, pct } from '../../lib/format';
import { useHeldResource, useResource } from '../../lib/hooks';
import type { Me } from '../../lib/types';
import type { AgentHealth, AgentRow, ClientHealthRow, ClientLink, ClientMapping } from './nocTypes';

/**
 * Estate health — every Automate agent, what is wrong with it, and whose it
 * is (api/src/lib/noc/agentHealth.ts).
 *
 * This is the dashboard the NOC module dropped on the way in, because the
 * standalone app computed it from a seeded generator and a dashboard of
 * invented numbers is worse than none. It is back because there is now an RMM
 * feed behind it: every figure here is read from Automate.
 *
 * Read once and KEPT until Refresh: one read is the whole estate in pages of
 * a thousand, and these numbers do not need to move while somebody is looking
 * at them.
 *
 * WHAT IT WILL NOT CLAIM. An agent that has not checked in cannot patch
 * itself, so it is counted as offline and nothing more — never as "behind on
 * patches" as well. Patching and antivirus are judged only on agents seen in
 * the last couple of days, and the captions say so where the figure appears
 * rather than in a footnote nobody reads.
 */

/** How strict to be, as three presets rather than three number boxes. */
const PRESETS = {
  standard: { offlineDays: 7, patchDays: 30, avDays: 7, label: 'Standard' },
  strict: { offlineDays: 3, patchDays: 14, avDays: 3, label: 'Strict' },
  relaxed: { offlineDays: 14, patchDays: 60, avDays: 14, label: 'Relaxed' },
} as const;
type PresetId = keyof typeof PRESETS;

const share = (part: number, whole: number) => (whole ? pct((part / whole) * 100, 1) : '—');
const ago = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'never');
const days = (n: number | null) => (n === null ? '—' : n === 0 ? 'today' : `${num(n)}d`);

export function NocAgentHealth() {
  const [preset, setPreset] = useState<PresetId>('standard');
  const [clientFilter, setClientFilter] = useState('');
  const [mapping, setMapping] = useState(false);
  const p = PRESETS[preset];
  const params = new URLSearchParams({
    offlineDays: String(p.offlineDays),
    patchDays: String(p.patchDays),
    avDays: String(p.avDays),
  });
  const live = useHeldResource<AgentHealth>(`/noc/agent-health?${params.toString()}`);
  const data = live.data;

  const controls = (
    <section className="card">
      <div className="noc-library-filters">
        <div className="field">
          <label>Thresholds</label>
          <Segmented
            label="How strict the thresholds are"
            value={preset}
            onChange={setPreset}
            options={(Object.keys(PRESETS) as PresetId[]).map((id) => ({ id, label: PRESETS[id].label }))}
          />
        </div>
        <div className="field grow">
          <label htmlFor="noc-health-client">Client</label>
          <input
            id="noc-health-client"
            type="search"
            className="input"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
            placeholder="Narrow the tables to one client…"
          />
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => setMapping((v) => !v)}>
          {mapping ? 'Hide mapping' : 'Client mapping'}
        </button>
        <button type="button" className="btn btn-secondary" disabled={live.refreshing} onClick={live.refresh}>
          {live.refreshing ? 'Reading Automate…' : 'Refresh'}
        </button>
      </div>
      <p className="xs muted mt-4">
        Live from ConnectWise Automate{data ? ` (${data.server})` : ''}. Offline past {p.offlineDays} days, Windows
        unpatched past {p.patchDays} days, virus definitions past {p.avDays} days. Patching and antivirus are judged
        only on agents seen in the last {data?.thresholds.seenDays ?? 2} days — a machine that is switched off cannot
        patch itself, and is counted as offline and nothing else. Agents in maintenance mode are counted but nothing
        is flagged against them.
        {live.fetchedAt &&
          ` As of ${new Date(live.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
      </p>
    </section>
  );

  if (live.loading && !data)
    return (
      <>
        {controls}
        <Loading label="Reading the estate from Automate — a large estate can take a minute" />
      </>
    );
  if (live.error)
    return (
      <>
        {controls}
        <ErrorBanner message={live.error} />
      </>
    );
  if (!data) return controls;
  if (data.totals.agents === 0)
    return (
      <>
        {controls}
        <Card>
          <p className="sub">Automate answered with no agents. Check the API user can see the computers.</p>
        </Card>
      </>
    );

  const t = data.totals;
  const term = clientFilter.trim().toLowerCase();
  const clients = term ? data.clients.filter((c) => c.client.toLowerCase().includes(term)) : data.clients;
  const attention = term ? data.attention.filter((a) => a.client.toLowerCase().includes(term)) : data.attention;

  return (
    <div className="noc-page">
      {controls}

      {/* Said before the figures, not after them: until the mapping is
          complete, a per-client number here is not the whole of that client,
          and a reader should know that before reading one.

          Only what is actually outstanding. An Automate client somebody has
          marked as never one of ours is just as unattributable, and is said
          so below — but it is not work, and a banner that goes on asking for
          a decision already made is one people learn to ignore. */}
      {t.unlinkedAgents - t.ignoredAgents > 0 && (
        <Card>
          <p className="sm">
            <strong>
              {num(t.unlinkedAgents - t.ignoredAgents)} agents (
              {share(t.unlinkedAgents - t.ignoredAgents, t.agents)}) across{' '}
              {num(t.unlinkedClients - t.ignoredClients)} Automate{' '}
              {t.unlinkedClients - t.ignoredClients === 1 ? 'client' : 'clients'} are not matched to a client of ours.
            </strong>{' '}
            They are counted in the totals above and listed under Automate’s own name for them, but nothing on this
            tab can be put beside those clients’ tickets, contracts or reviews until the mapping is made.
            {t.ignoredAgents > 0 &&
              ` A further ${num(t.ignoredAgents)} agents are under Automate clients marked as never ours.`}
          </p>
          <button type="button" className="btn btn-secondary mt-4" onClick={() => setMapping((v) => !v)}>
            {mapping ? 'Hide the client mapping' : 'Open the client mapping'}
          </button>
        </Card>
      )}

      {mapping && <ClientMappingCard onClose={() => setMapping(false)} onSaved={live.refresh} />}

      <div className="grid grid-4">
        <Stat label="Agents" value={num(t.agents)} foot={`${num(t.servers)} servers · ${num(t.workstations)} workstations`} />
        <Stat label="Online now" value={share(t.online, t.agents)} foot={`${num(t.online)} of ${num(t.agents)} checking in`} />
        <Stat
          label={`Offline over ${data.thresholds.offlineDays} days`}
          value={num(t.offlineTooLong)}
          foot={t.neverSeen ? `${num(t.neverSeen)} have never checked in` : 'none never seen'}
          flagged={t.offlineTooLong > 0}
        />
        <Stat
          label="Nothing wrong"
          value={share(t.healthy, t.agents)}
          foot={`${num(t.agents - t.healthy)} agents need something`}
        />
        <Stat
          label={`Unpatched over ${data.thresholds.patchDays} days`}
          value={num(t.patchStale)}
          foot={`of ${num(t.patchJudged)} Windows agents seen recently`}
          flagged={t.patchStale > 0}
        />
        <Stat
          label="No patch date"
          value={num(t.patchUnknown)}
          foot="Windows has never reported an update"
          flagged={t.patchUnknown > 0}
        />
        <Stat
          label={`Definitions over ${data.thresholds.avDays} days`}
          value={num(t.avStale)}
          foot={`${num(t.avMissing)} with no antivirus reported at all`}
          flagged={t.avStale + t.avMissing > 0}
        />
        <Stat label="Reboot needed" value={num(t.rebootNeeded)} foot={share(t.rebootNeeded, t.agents) + ' of the estate'} flagged={t.rebootNeeded > 0} />
      </div>

      <ChartCard
        title="How long the offline agents have been gone"
        subtitle={`${num(t.offline)} agents are not checking in`}
        table={{
          columns: [
            { key: 'label', label: 'Gone for' },
            { key: 'agents', label: 'Agents', align: 'right', render: (r: { label: string; agents: number }) => num(r.agents) },
            {
              key: 'share',
              label: 'Share of offline',
              align: 'right',
              render: (r: { label: string; agents: number }) => share(r.agents, t.offline),
            },
          ],
          rows: data.offlineBuckets,
          rowKey: (r) => r.label,
        }}
        footer="An agent gone under a day is usually a laptop that went home. The long tail is the work."
      >
        <BarChart
          data={data.offlineBuckets}
          xKey="label"
          series={[{ key: 'agents', label: 'Agents', color: 'var(--series-1)' }]}
          yFormat={(v) => num(v)}
          height={220}
          emptyMessage="Every agent is checking in."
        />
      </ChartCard>

      <div className="noc-split-5-7">
        <ChartCard
          title="Agents by client"
          subtitle={`Top 15 of ${num(clients.length)} by estate size`}
          table={{
            columns: [
              { key: 'client', label: 'Client', render: (r: ClientHealthRow) => <ClientName row={r} /> },
              { key: 'agents', label: 'Agents', align: 'right', render: (r: ClientHealthRow) => num(r.agents) },
              { key: 'offline', label: 'Offline', align: 'right', render: (r: ClientHealthRow) => num(r.offline) },
              { key: 'patchStale', label: 'Unpatched', align: 'right', render: (r: ClientHealthRow) => num(r.patchStale) },
              { key: 'avStale', label: 'AV stale', align: 'right', render: (r: ClientHealthRow) => num(r.avStale) },
              { key: 'rebootNeeded', label: 'Reboot', align: 'right', render: (r: ClientHealthRow) => num(r.rebootNeeded) },
              { key: 'healthy', label: 'Clean', align: 'right', render: (r: ClientHealthRow) => share(r.healthy, r.agents) },
            ],
            rows: clients,
            rowKey: (r) => r.key,
            empty: 'No client matches that search.',
          }}
        >
          <BarList
            rows={clients.slice(0, 15).map((c) => ({
              name: c.client,
              sub: c.clientId === null ? (c.ignored ? 'never ours' : 'not linked') : `${share(c.healthy, c.agents)} clean`,
              value: c.agents,
            }))}
          />
        </ChartCard>

        <ChartCard
          title="Where the problems are"
          subtitle="Clients with the most agents needing attention"
          table={{
            columns: [
              { key: 'client', label: 'Client', render: (r: ClientHealthRow) => <ClientName row={r} /> },
              { key: 'needing', label: 'Needing attention', align: 'right', render: (r: ClientHealthRow) => num(r.agents - r.healthy) },
              { key: 'agents', label: 'Agents', align: 'right', render: (r: ClientHealthRow) => num(r.agents) },
              { key: 'share', label: 'Share', align: 'right', render: (r: ClientHealthRow) => share(r.agents - r.healthy, r.agents) },
            ],
            rows: [...clients].sort((a, b) => b.agents - b.healthy - (a.agents - a.healthy)),
            rowKey: (r) => r.key,
            empty: 'No client matches that search.',
          }}
          footer="Count, not share: one client with forty broken agents is the afternoon's work, whatever its percentage."
        >
          <BarList
            color="var(--series-3)"
            rows={[...clients]
              .sort((a, b) => b.agents - b.healthy - (a.agents - a.healthy))
              .slice(0, 15)
              .filter((c) => c.agents - c.healthy > 0)
              .map((c) => ({
                name: c.client,
                sub: `of ${num(c.agents)} agents`,
                value: c.agents - c.healthy,
              }))}
          />
        </ChartCard>
      </div>

      <Card
        title="Agents needing attention"
        subtitle={
          /* The server sends the worst few hundred, not all of them, and the
             client box filters what it sent. So the caption counts against
             THAT list rather than against the estate — otherwise filtering to
             a client with 275 flagged agents would report the 44 of them that
             happened to make the cut, which reads as good news. */
          term
            ? `${num(attention.length)} of the worst ${num(data.attention.length)} match “${clientFilter.trim()}”`
            : `The worst ${num(data.attention.length)} of ${num(data.attentionTotal)}`
        }
      >
        <AttentionTable rows={attention} />
        {data.attentionTotal > data.attention.length && (
          <p className="xs subtle mt-4">
            {num(data.attentionTotal)} agents are flagged in all
            {term ? `, across every client — this list shows the worst ${num(data.attention.length)}` : ''}. Work the
            top down, or loosen the thresholds to see fewer.
          </p>
        )}
      </Card>
    </div>
  );
}

/**
 * A client's name in a table. An unlinked row says so on the row rather than
 * being footnoted: it is Automate's name for a company this app does not know
 * it has, and reading it as one of ours is the mistake to prevent.
 */
function ClientName({ row }: { row: ClientHealthRow }) {
  return (
    <>
      {row.client}
      {row.clientId === null ? (
        <span className="ui-cell-sub">{row.ignored ? 'Marked never ours' : 'Automate only — not linked'}</span>
      ) : row.automateClientIds.length > 1 ? (
        <span className="ui-cell-sub">{row.automateClientIds.length} Automate clients</span>
      ) : null}
    </>
  );
}

/**
 * The mapping editor: which of our clients each Automate client is.
 *
 * Admin-only to change — the mapping decides whose agents these are. Everyone
 * else sees it read-only, because "why is this client's estate empty" is a
 * question the answer to should be visible without an admin.
 */
function ClientMappingCard({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const mapping = useResource<ClientMapping>('/noc/automate-clients');
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(link: ClientLink, value: string) {
    setSaving(link.automateClientId);
    setError(null);
    try {
      await api.put('/noc/automate-clients', {
        automateClientId: link.automateClientId,
        automateClientName: link.automateClientName,
        clientId: value === '' || value === 'ignore' ? null : Number(value),
        ignore: value === 'ignore',
      });
      mapping.reload();
      /* The grouping on the tab behind this card is now wrong until it is
         read again — the API has already thrown its held copy away. */
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that link');
    } finally {
      setSaving(null);
    }
  }

  const links = mapping.data?.links ?? [];
  /* Three states, counted as three. Folding "never ours" in with "matched"
     would report a complete mapping to somebody who still has clients to
     match, which is the one thing this caption exists to tell them. */
  const matched = links.filter((l) => l.clientId !== null).length;
  const ignoredCount = links.filter((l) => l.clientId === null && l.how === 'ignored').length;
  const unmatched = links.length - matched - ignoredCount;

  return (
    <Card
      title="Client mapping"
      subtitle={
        mapping.data
          ? `${num(links.length)} Automate clients · ${num(matched)} matched, ${num(unmatched)} not` +
            (ignoredCount ? `, ${num(ignoredCount)} never ours` : '')
          : 'Which of our clients each Automate client is'
      }
      actions={
        <button type="button" className="btn btn-ghost" onClick={onClose}>
          Close
        </button>
      }
    >
      {mapping.error && <ErrorBanner message={mapping.error} />}
      {error && <ErrorBanner message={error} />}
      {mapping.loading && !mapping.data && <Loading label="Reading Automate’s clients" />}

      {mapping.data && (
        <>
          <p className="xs muted mb-4">
            Matched automatically where exactly one of our clients has the name, once capitals, punctuation and
            endings like LLC are set aside — the same rule the Autotask company link uses. A name two of our clients
            share is never matched automatically. A choice made here is never overwritten.
            {!isAdmin && ' Only an admin can change these.'}
          </p>
          <DataTable
            columns={[
              {
                key: 'automate',
                label: 'In Automate',
                render: (l: ClientLink) => (
                  <>
                    {l.automateClientName}
                    <span className="ui-cell-sub">
                      #{l.automateClientId}
                      {l.linkedAsName ? ` · linked as “${l.linkedAsName}”` : ''}
                    </span>
                  </>
                ),
              },
              {
                key: 'ours',
                label: 'Our client',
                render: (l: ClientLink) =>
                  isAdmin ? (
                    <select
                      className="select"
                      value={l.clientId === null ? (l.how === 'ignored' ? 'ignore' : '') : String(l.clientId)}
                      disabled={saving !== null}
                      aria-label={`Our client for ${l.automateClientName}`}
                      onChange={(e) => void save(l, e.target.value)}
                    >
                      <option value="">Not matched</option>
                      <option value="ignore">Never one of ours</option>
                      {mapping.data!.ourClients.map((c) => (
                        <option key={c.clientId} value={c.clientId}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <>{l.clientName ?? (l.how === 'ignored' ? 'Never one of ours' : 'Not matched')}</>
                  ),
              },
              {
                key: 'how',
                label: 'Matched',
                render: (l: ClientLink) =>
                  l.how === null ? (
                    <StatusChip band="warning">Not matched</StatusChip>
                  ) : l.how === 'ignored' ? (
                    <StatusChip band="neutral">Ignored</StatusChip>
                  ) : (
                    <StatusChip band="good">{l.how === 'auto' ? 'By name' : 'By hand'}</StatusChip>
                  ),
              },
            ]}
            rows={links}
            rowKey={(l) => String(l.automateClientId)}
            empty="Automate reports no clients."
          />
        </>
      )}
    </Card>
  );
}

/**
 * The list an engineer works down. One row per agent, every reason it is here
 * spelled out — a column of ticks would need a legend, and the reasons are
 * short enough to just say.
 */
function AttentionTable({ rows }: { rows: AgentRow[] }) {
  return (
    <DataTable
      columns={[
        {
          key: 'name',
          label: 'Agent',
          render: (r: AgentRow) => (
            <>
              {r.name || `#${r.id}`}
              <span className="ui-cell-sub">
                {r.client}
                {r.location ? ` · ${r.location}` : ''}
              </span>
            </>
          ),
        },
        {
          key: 'os',
          label: 'What it is',
          render: (r: AgentRow) => (
            <>
              {r.type || '—'}
              <span className="ui-cell-sub">{r.os || 'unknown OS'}</span>
            </>
          ),
        },
        {
          key: 'lastContact',
          label: 'Last seen',
          align: 'right',
          render: (r: AgentRow) => (
            <>
              {days(r.offlineDays)}
              <span className="ui-cell-sub">{ago(r.lastContact)}</span>
            </>
          ),
        },
        { key: 'patchAgeDays', label: 'Patched', align: 'right', render: (r: AgentRow) => days(r.patchAgeDays) },
        {
          key: 'av',
          label: 'Definitions',
          align: 'right',
          render: (r: AgentRow) => (
            <>
              {days(r.avAgeDays)}
              <span className="ui-cell-sub">{r.virusScanner || 'none reported'}</span>
            </>
          ),
        },
        { key: 'flags', label: 'Why', render: (r: AgentRow) => r.flags.join(' · ') },
      ]}
      rows={rows}
      rowKey={(r) => String(r.id)}
      empty="Nothing is flagged. Every agent is checking in, patched and protected."
    />
  );
}
